import React from 'react';
import Check from '@mui/icons-material/Check';
import {
    extractBmiValue,
    formatBmiInline,
} from '../../components/general/SmartFields/normaliseInitialListElements';
import { parseSurgicalProcedureName } from '../../utils/surgicalProcedure';
import { formatSmartfieldValue } from '../../utils/formatSmartfieldItem';
import { DIAGNOSIS_TYPE_ALIASES } from '../../constants';
import { Translate } from 'react-localize-redux';
import { VPA_OUTCOME_FIELD_TYPE, vpaOutcomeLabelId } from '../../utils/vpaOutcome';

/**
 * SHER-210 (Pedro 2026-05-04) — extracted out of SurveyRecordsTable.tsx so the
 * BMI crash path has a unit test. The original `renderValue` had cases for
 * textarea/multioption/checkbox and a `default: return value` that returned
 * the raw saved BMI object {bmi, bmi_height, bmi_weight} as a React child →
 * React error #31 "Objects are not valid as a React child" on
 * /patient/.../nurse when a nursing submission contained a BMI field.
 *
 * Safer behaviour now:
 *   - explicit `bmi` case → formatted string ("22.50 (170 cm / 65 kg)")
 *   - default → if value is an object/array, fall back to "—" rather than
 *     returning it raw. Any future smartfield type that lands here without a
 *     dedicated case will render a placeholder instead of crashing the page.
 */
export function renderValue(value: any, type: string): React.ReactNode {
    // SHER-1130: diagnosis (ICD / ICT / ICD-11, all three aliases) and file
    // values are stored as an array of objects, e.g. [{ict, "ict-code"}] or
    // [{file, file_type}]. Without a case they fell through to the default
    // branch below, whose `typeof value === 'object'` guard rendered them as
    // the "—" placeholder — swallowing present clinical data. Route them through
    // the shared smartfield formatter so the table shows "Name (CODE)" / the
    // file, exactly like the single-submission detail view (show_records_section).
    if (DIAGNOSIS_TYPE_ALIASES.includes(type) || type === 'file') {
        const formatted = formatSmartfieldValue(value);
        return formatted === '' ? '—' : formatted;
    }
    switch (type) {
        case 'textarea':
            return <div dangerouslySetInnerHTML={{ __html: value }}></div>;
        case 'multioption':
            // Defensive: legacy rows can have value === null even though the type is multioption.
            if (Array.isArray(value) && value.length > 0 && value[0] && typeof value[0].multioption !== 'undefined') {
                return value[0].multioption;
            }
            return '—';
        case 'checkbox':
            return <Check checked={value === 'true'} />;
        case 'bmi':
            return formatBmiInline(extractBmiValue(value));
        case 'surgical_procedure':
            // SHER-459: stored as { procedure, procedureId } (or a JSON string of
            // it); show the procedure name instead of "—"/raw JSON.
            return parseSurgicalProcedureName(value) || '—';
        case VPA_OUTCOME_FIELD_TYPE: {
            // SHER-759: the VPA decision, worded like the waiting list.
            const labelId = vpaOutcomeLabelId(value);
            return labelId ? <Translate id={labelId} /> : '—';
        }
        default:
            // Anything that's not a primitive can't be rendered as a React child.
            // Returning it raw is what crashed the /nurse page (React #31).
            if (value == null) return '';
            if (typeof value === 'object') return '—';
            return value;
    }
}
